import logError from '@alltrails/core/utils/logError';
import { CustomRouteDetails, CustomRouteMap, ActivityType } from '../../types/CustomRoute';
import getMapElevationData from '../getMapElevationData';
import { decodeSegmentLatLng } from '../legacyGeoJSONConversions';
import { ElevationPoint, MapWithRoutes } from '../../types/Geo';
import { getLineColor } from './colors';

export type MapResponse = { maps: CustomRouteMap[]; meta: { status: string; items: number; timestamp: string } };

export type CustomRouteResponse = {
  route: CustomRouteDetails;
  map: CustomRouteMap;
};

const transformMapForElevationData = (map: CustomRouteMap): Partial<MapWithRoutes> => {
  return {
    routes: map.routes.map(route => ({
      lineSegments: route.lineSegments.map(segment => ({
        sequence_num: segment.sequence_num,
        polyline: segment.polyline,
        freeDraw: segment.freeDraw
      }))
    }))
  };
};

export const transformCustomRouteMap = (map: CustomRouteMap): CustomRouteResponse => {
  const {
    id,
    slug,
    bounds: rawBounds,
    name: rawName,
    description: rawDescription,
    contentPrivacy,
    activity,
    summaryStats,
    estimatedTimeToComplete,
    metadata,
    location
  } = map;
  const activityType = (activity?.uid as ActivityType) ?? null;
  const { distanceTotal: distance, elevationGain } = summaryStats;
  const { created: createdAt } = metadata;
  const name = rawName || undefined;
  const description = rawDescription || undefined;
  const estimatedTime = estimatedTimeToComplete || undefined;

  const bounds = {
    longitudeTopLeft: Number(rawBounds.longitudeTopLeft),
    latitudeTopLeft: Number(rawBounds.latitudeTopLeft),
    longitudeBottomRight: Number(rawBounds.longitudeBottomRight),
    latitudeBottomRight: Number(rawBounds.latitudeBottomRight)
  };

  const elevationData = getMapElevationData(transformMapForElevationData(map));
  const routes = map.routes || [];

  if (elevationData.length !== routes.length) {
    logError('Elevation data does not match the number of routes');
  }

  const segments = [...routes]
    .sort((a, b) => a.sequence_num - b.sequence_num)
    .map(route => {
      const { id: serverId, sequence_num, lineGeoStats, lineDisplayProperty, lineSegments, estimatedTimeToComplete } = route;
      const id = (sequence_num || 1) - 1;
      const { distanceTotal: distance, elevationGain } = lineGeoStats;
      const rawColor = lineDisplayProperty?.color;
      const color = rawColor ? getLineColor(rawColor) : undefined;
      const distanceOffset = elevationData[id]?.[0]?.[0] ?? 0;
      const elevationPoints = (elevationData[id] || []).map(
        // Make distanceTo relative to the segment rather than to the start of the route
        // This makes our state easier to manage when adding and removing segments, paths, and points
        ([distanceTo, ...rest]) => [distanceTo - distanceOffset, ...rest] as ElevationPoint
      );
      const paths = [...lineSegments]
        .sort((a, b) => a.sequence_num - b.sequence_num)
        .map(segment => ({ points: decodeSegmentLatLng(segment), freeDraw: segment.freeDraw ?? undefined }));

      return { id, serverId, color, elevationPoints, paths, distance, elevationGain, estimatedTime: estimatedTimeToComplete };
    });

  const waypoints = [...map.waypoints]
    .sort((a, b) => a.order - b.order)
    .map(waypoint => {
      const { id: serverId, order, name, description, location, waypointDisplayProperty } = waypoint;
      const id = (order || 1) - 1;
      const { latitude, longitude } = location;
      const { showTitle } = waypointDisplayProperty;

      return { id, serverId, name, description, latitude, longitude, showTitle };
    });

  return {
    route: {
      id,
      slug,
      bounds,
      name,
      description,
      contentPrivacy,
      activityType,
      distance,
      elevationGain,
      estimatedTime,
      segments,
      waypoints,
      createdAt,
      location
    },
    map
  };
};

export const transformCustomRouteMapResponse = (response: Omit<MapResponse, 'meta'>): CustomRouteResponse => {
  const map = response.maps[0];
  return transformCustomRouteMap(map);
};
